import React, { useEffect, useState, useCallback } from 'react';
import AdminShell from './AdminShell';
import { useAdminI18n } from './i18n';
import { getMe, listUsers, createUser, updateUser } from './adminApi';
import './admin.css';

const Switch = ({ checked, disabled, onChange }) => (
  <label className={`switch ${disabled ? 'disabled' : ''}`}>
    <input type="checkbox" checked={!!checked} disabled={disabled}
      onChange={(e) => !disabled && onChange(e.target.checked)} />
    <span className="slider" />
  </label>
);

const AdminDashboard = () => {
  const { t } = useAdminI18n();
  const [authState, setAuthState] = useState('checking');
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);

  const [newIdentity, setNewIdentity] = useState('');
  const [newAdmin, setNewAdmin] = useState(false);
  const [newEdit, setNewEdit] = useState(false);
  const [adding, setAdding] = useState(false);

  const flash = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  const load = useCallback(async (term = '') => {
    setLoading(true);
    try {
      const data = await listUsers(term);
      setUsers(data.users || []);
    } catch (e) {
      flash('error', t('Failed to load users.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    getMe()
      .then((d) => {
        setMe(d);
        if (d && d.is_admin && d.is_active) {
          setAuthState('ok');
          load();
        } else {
          setAuthState('denied');
        }
      })
      .catch(() => setAuthState('denied'));
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const patch = async (user, payload) => {
    try {
      const updated = await updateUser(user.id, payload);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (e) {
      flash('error', e?.response?.data?.detail || t('Update failed.'));
    }
  };

  const onAdd = async (e) => {
    e.preventDefault();
    if (!newIdentity.trim()) return;
    setAdding(true);
    try {
      await createUser({ identity: newIdentity.trim(), is_admin: newAdmin, is_edit_access: newEdit });
      flash('success', `${newIdentity.trim()} ✔`);
      setNewIdentity('');
      setNewAdmin(false);
      setNewEdit(false);
      load(search);
    } catch (e2) {
      flash('error', e2?.response?.data?.detail || t('Could not add user.'));
    } finally {
      setAdding(false);
    }
  };

  if (authState === 'checking') {
    return <AdminShell title={t('User Management')}><div className="admin-loading">{t('Checking access…')}</div></AdminShell>;
  }
  if (authState === 'denied') {
    return (
      <AdminShell title={t('User Management')}>
        <div className="admin-denied">
          <h2>{t('Access denied')}</h2>
          <p>{t('You need admin rights to view this page.')}</p>
        </div>
      </AdminShell>
    );
  }

  const adminCount = users.filter((u) => u.is_admin).length;

  return (
    <AdminShell title={t('User Management')}>
      {banner && <div className={`admin-banner ${banner.type}`}>{banner.text}</div>}

      <div className="metric-grid">
        <div className="metric-card"><div className="metric-label">{t('Total Users')}</div><div className="metric-value">{users.length}</div></div>
        <div className="metric-card"><div className="metric-label">{t('Admins')}</div><div className="metric-value">{adminCount}</div></div>
        <div className="metric-card"><div className="metric-label">{t('Active')}</div><div className="metric-value">{users.filter((u) => u.is_active).length}</div></div>
        <div className="metric-card"><div className="metric-label">{t('Edit Access')}</div><div className="metric-value">{users.filter((u) => u.is_edit_access).length}</div></div>
      </div>

      {/* Provision */}
      <div className="admin-panel">
        <h2>{t('Provision a user')}</h2>
        <form className="admin-toolbar" onSubmit={onAdd}>
          <div className="admin-field" style={{ flex: 1, minWidth: 240 }}>
            <label>{t('Email or Business ID (short id)')}</label>
            <input className="admin-input" placeholder={t('e.g. abbiswa or jane@corp.com')}
              value={newIdentity} onChange={(e) => setNewIdentity(e.target.value)} />
          </div>
          <div className="admin-field">
            <label>{t('Make admin')}</label>
            <Switch checked={newAdmin} onChange={setNewAdmin} />
          </div>
          <div className="admin-field">
            <label>{t('Edit access')}</label>
            <Switch checked={newEdit} onChange={setNewEdit} />
          </div>
          <button className="admin-btn" type="submit" disabled={adding || !newIdentity.trim()}>
            {adding ? t('Adding…') : t('Add user')}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="admin-panel">
        <h2>{t('All users')}</h2>
        <form className="admin-toolbar" onSubmit={onSearch}>
          <div className="admin-field" style={{ flex: 1, minWidth: 240 }}>
            <label>{t('Search')}</label>
            <input className="admin-input" placeholder={t('Search by email or id')}
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="admin-btn secondary" type="submit">{t('Search')}</button>
          {search && (
            <button className="admin-btn secondary" type="button"
              onClick={() => { setSearch(''); load(''); }}>{t('Clear')}</button>
          )}
        </form>

        {loading ? (
          <div className="admin-empty">{t('Loading…')}</div>
        ) : users.length === 0 ? (
          <div className="admin-empty">{t('No users found.')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('User')}</th>
                  <th>{t('Type')}</th>
                  <th>{t('Admin')}</th>
                  <th>{t('Access')}</th>
                  <th>{t('Active')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = me && u.id === me.id;
                  const display = u.user_email || u.short_id || u.id;
                  return (
                    <tr key={u.id}>
                      <td>
                        <strong>{display}</strong>
                        {isSelf && <span className="pill admin" style={{ marginLeft: 8 }}>{t('you')}</span>}
                      </td>
                      <td>
                        <span className="pill readonly">{u.user_email ? t('email') : t('short id')}</span>
                      </td>
                      <td>
                        <Switch checked={u.is_admin} disabled={isSelf}
                          onChange={(v) => patch(u, { is_admin: v })} />
                      </td>
                      <td>
                        <div className="cell-control">
                          <Switch checked={u.is_edit_access}
                            onChange={(v) => patch(u, { is_edit_access: v })} />
                          <span className={`pill ${u.is_edit_access ? 'edit' : 'readonly'}`}>
                            {u.is_edit_access ? t('edit') : t('read-only')}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-control">
                          <Switch checked={u.is_active} disabled={isSelf}
                            onChange={(v) => patch(u, { is_active: v })} />
                          <span className={`pill ${u.is_active ? 'active' : 'inactive'}`}>
                            {u.is_active ? t('active') : t('disabled')}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminDashboard;
